import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { AuthContext } from "../context/AuthProvider";
import { message, Alert, Tooltip } from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import clientModel from "../lib/firebase/clientModel";
import logo512 from "../assets/logo.png";

export default function Widget() {
  const { user, userInfo } = useContext(AuthContext);
  const [client, setClient] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch the client/company associated with the user
  useEffect(() => {
    const fetchClient = async () => {
      if (user?.uid) {
        const foundClient = await clientModel.getByAuthorizedUserId(user.uid);
        setClient(foundClient);
      }
    };
    fetchClient();
  }, [user]);

  // Generate the widget script tag based on client info
  const generateWidgetScript = () => {
    // Use the Firestore document ID as the client key
    const clientKey = client?.id || "your-client-key";
    return `<script async src="https://api.clumse.com/widget.js" data-client-key="${clientKey}"></script>`;
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(generateWidgetScript());
      setCopied(true);
      message.success("Widget code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error("Failed to copy code. Please select and copy manually.");
    }
  };

  const handleCopyAll = async () => {
    const fullImplementation = `<!-- Clumse Widget Implementation -->
<!-- Add this code right before the closing </body> tag on your website -->
${generateWidgetScript()}`;

    try {
      await navigator.clipboard.writeText(fullImplementation);
      setCopied(true);
      message.success("Full implementation code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error("Failed to copy code. Please select and copy manually.");
    }
  };

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Title>Widget Implementation</Title>
          <Subtitle>
            Add the Clumse AI assistant to your website in minutes
          </Subtitle>
        </HeaderContent>
      </Header>

      {!client && (
        <Alert
          message="No company profile found"
          description="Please complete your profile setup to get your widget code."
          type="warning"
          showIcon
          style={{ maxWidth: 700, margin: "2rem auto" }}
        />
      )}

      <ContentCard>
        <SectionTitle>Implementation Instructions</SectionTitle>

        <InstructionsSection>
          <StepNumber>1</StepNumber>
          <StepContent>
            <StepTitle>Locate your website's main HTML file</StepTitle>
            <StepDescription>
              Find your website's main HTML file (usually called{" "}
              <CodeInline>index.html</CodeInline> or similar)
            </StepDescription>
          </StepContent>
        </InstructionsSection>

        <InstructionsSection>
          <StepNumber>2</StepNumber>
          <StepContent>
            <StepTitle>Find the closing body tag</StepTitle>
            <StepDescription>
              Look for the <CodeInline>&lt;/body&gt;</CodeInline> tag near the
              end of your HTML file
            </StepDescription>
          </StepContent>
        </InstructionsSection>

        <InstructionsSection>
          <StepNumber>3</StepNumber>
          <StepContent>
            <StepTitle>Add the widget code</StepTitle>
            <StepDescription>
              Copy and paste the code below right before the closing{" "}
              <CodeInline>&lt;/body&gt;</CodeInline> tag
            </StepDescription>
          </StepContent>
        </InstructionsSection>

        <CodeSection>
          <CodeHeader>
            <CodeTitle>Your Widget Code</CodeTitle>
            <CodeActions>
              <Tooltip title="Copy widget code only">
                <CopyButton onClick={handleCopyCode} $copied={copied}>
                  {copied ? <CheckOutlined /> : <CopyOutlined />}
                  {copied ? "Copied!" : "Copy Code"}
                </CopyButton>
              </Tooltip>
              <Tooltip title="Copy full implementation with comments">
                <CopyAllButton onClick={handleCopyAll} $copied={copied}>
                  {copied ? <CheckOutlined /> : <CopyOutlined />}
                  {copied ? "Copied!" : "Copy All"}
                </CopyAllButton>
              </Tooltip>
            </CodeActions>
          </CodeHeader>

          <CodeBlock>
            <CodeContent>{generateWidgetScript()}</CodeContent>
          </CodeBlock>

          <CodeNote>
            <InfoCircleOutlined
              style={{ marginRight: "8px", color: "#CDAF63" }}
            />
            The script should be placed just before the closing body tag to
            ensure optimal performance.
          </CodeNote>
        </CodeSection>

        <ImplementationExample>
          <ExampleTitle>Example Implementation</ExampleTitle>
          <ExampleDescription>
            Here's how the code should look in your HTML file:
          </ExampleDescription>

          <ExampleCodeBlock>
            <ExampleCodeContent>
              {`
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Your Clinic Website</title>
</head>
<body>
  <!-- Your website content here -->

  <!-- Clumse Widget Implementation -->
  <!-- Add this code right before the closing </body> tag -->
  ${generateWidgetScript()}
</body>
</html>
`.trim()}
            </ExampleCodeContent>
          </ExampleCodeBlock>
        </ImplementationExample>

        <HelpSection>
          <HelpTitle>Need Help?</HelpTitle>
          <HelpDescription>
            Our Clumse team is here to help you get set up and make the most of
            your AI-powered assistant:
          </HelpDescription>
          <HelpOptions>
            <HelpOption>
              <HelpIcon>
                <img
                  src={logo512}
                  alt="Clumse logo"
                  style={{ width: 38, height: 38, borderRadius: "50%" }}
                />
              </HelpIcon>
              <HelpContent>
                <HelpOptionTitle>Email Support</HelpOptionTitle>
                <HelpOptionDescription>
                  Contact us at{" "}
                  <HelpLink href="mailto:support@clumse.com">
                    support@clumse.com
                  </HelpLink>
                </HelpOptionDescription>
              </HelpContent>
            </HelpOption>
          </HelpOptions>
        </HelpSection>

        <SuccessSection>
          <SuccessTitle>What Happens Next?</SuccessTitle>
          <SuccessDescription>
            Once implemented, the Clumse widget will automatically appear on
            your website and start helping you capture, qualify, and delight
            your clients 24/7.
          </SuccessDescription>

          <SuccessFeatures>
            <SuccessFeature>
              <FeatureIcon>🤖</FeatureIcon>
              <FeatureText>AI-powered chat assistant</FeatureText>
            </SuccessFeature>
            <SuccessFeature>
              <FeatureIcon>📱</FeatureIcon>
              <FeatureText>Mobile-responsive design</FeatureText>
            </SuccessFeature>
            <SuccessFeature>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureText>Instant lead qualification</FeatureText>
            </SuccessFeature>
            <SuccessFeature>
              <FeatureIcon>📊</FeatureIcon>
              <FeatureText>Real-time analytics</FeatureText>
            </SuccessFeature>
          </SuccessFeatures>
        </SuccessSection>
      </ContentCard>
      <LastSectionSpacer />
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f0fdf4 0%, #f8fbef 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(44, 95, 45, 0.03) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(134, 228, 157, 0.04) 0%,
        transparent 50%
      );
    pointer-events: none;
  }
`;

const Header = styled.div`
  padding: 4rem 2rem 3rem 2rem;
  background: linear-gradient(135deg, #2c5f2d 0%, #1a3a1b 100%);
  color: white;
  text-align: center;
  width: 100%;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.2);
  border-bottom: 3px solid #86e49d;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        ellipse at 30% 20%,
        rgba(134, 228, 157, 0.08) 0%,
        transparent 50%
      ),
      radial-gradient(
        ellipse at 70% 80%,
        rgba(255, 255, 255, 0.03) 0%,
        transparent 50%
      );
    pointer-events: none;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family:
    "Inter",
    system-ui, ui-sans-serif, sans-serif;
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  letter-spacing: -0.02em;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: #ffffff;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.25rem;
  margin: 0 0 2rem 0;
  opacity: 0.9;
  letter-spacing: -0.01em;
  color: #d4f5db;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeaderDecoration = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const DecorationLine = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.6),
    transparent
  );

  @media (max-width: 768px) {
    width: 40px;
  }
`;

const DecorationDot = styled.div`
  width: 8px;
  height: 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
`;

const ContentCard = styled.div`
  background: #fff;
  border: 2px solid #d1f0d9;
  border-radius: 24px;
  box-shadow:
    0 12px 48px rgba(44, 95, 45, 0.08),
    0 4px 16px rgba(0, 0, 0, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  padding: 4rem 4rem 4rem 4rem;
  margin: 3rem auto 4rem auto;
  max-width: 1000px;
  width: 100%;
  color: #222;
  position: relative;
  @media (max-width: 900px) {
    padding: 2.5rem 1.25rem;
    margin: 2rem 0.5rem 3rem 0.5rem;
    border-radius: 18px;
  }
  @media (max-width: 600px) {
    padding: 1rem 0.25rem;
    margin: 1rem 0.1rem 2rem 0.1rem;
    border-radius: 12px;
  }
`;

const SectionTitle = styled.h2`
  font-family:
    "Inter",
    system-ui, ui-sans-serif, sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c5f2d;
  margin-bottom: 3rem;
  letter-spacing: -0.01em;
  text-align: left;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    bottom: -0.75rem;
    left: 0;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #86e49d, #2c5f2d);
    border-radius: 2px;
  }
  @media (max-width: 900px) {
    font-size: 2rem;
    margin-bottom: 2rem;
  }
  @media (max-width: 600px) {
    font-size: 1.2rem;
    margin-bottom: 1.1rem;
    &::after {
      width: 36px;
      height: 2px;
      left: 50%;
      transform: translateX(-50%);
    }
    text-align: center;
  }
`;

const InstructionsSection = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2.5rem;
  padding: 2rem;
  background: linear-gradient(135deg, #f0fdf4 0%, #f8fbef 100%);
  border-radius: 20px;
  border: 2px solid rgba(44, 95, 45, 0.12);
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.10);
    border-color: rgba(44, 95, 45, 0.2);
  }
`;

const StepNumber = styled.div`
  background: linear-gradient(135deg, #2c5f2d 0%, #1a3a1b 100%);
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.3rem;
  margin-right: 2rem;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(44, 95, 45, 0.3);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, #86e49d, #2c5f2d);
    border-radius: 50%;
    z-index: -1;
    opacity: 0.3;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.75rem;
`;

const StepDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const CodeInline = styled.code`
  background: #f0fdf4;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-family: var(--font-stack-mono);
  font-size: 0.95rem;
  color: #2c5f2d;
  font-weight: 600;
  border: 1px solid rgba(44, 95, 45, 0.2);
`;

const CodeSection = styled.div`
  margin: 4rem 0;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.7rem;
  }
`;

const CodeTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
  margin: 0;
  @media (max-width: 600px) {
    margin-bottom: 0.3rem;
    width: 100%;
    text-align: center;
  }
`;

const CodeActions = styled.div`
  display: flex;
  gap: 1rem;
  @media (max-width: 600px) {
    flex-direction: row;
    gap: 0.7rem;
    width: 100%;
    align-items: stretch;
    justify-content: flex-start;
    button {
      width: 100%;
      min-width: 0;
      flex: 1 1 50%;
    }
  }
`;

const CopyButton = styled.button`
  background: linear-gradient(135deg, #2c5f2d 0%, #1a3a1b 100%);
  color: white;
  border: none;
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: "Inter", sans-serif;
  box-shadow: 0 4px 12px rgba(44, 95, 45, 0.25);

  &:hover {
    background: linear-gradient(135deg, #1a3a1b 0%, #2c5f2d 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(44, 95, 45, 0.35);
  }
`;

const CopyAllButton = styled(CopyButton)`
  background: linear-gradient(135deg, #666 0%, #444 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  &:hover {
    background: linear-gradient(135deg, #444 0%, #666 100%);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }
`;

const CodeBlock = styled.div`
  background: #2d3748;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 2px solid #4a5568;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CodeContent = styled.pre`
  color: #e2e8f0;
  font-family: var(--font-stack-mono);
  font-size: 1.05rem;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
`;

const CodeNote = styled.div`
  display: flex;
  align-items: center;
  font-family: "Inter", sans-serif;
  font-size: 1rem;
  color: #666;
  font-style: italic;
  padding: 1rem 1.5rem;
  background: rgba(44, 95, 45, 0.04);
  border-radius: 12px;
  border-left: 4px solid #86e49d;
`;

const ImplementationExample = styled.div`
  margin: 4rem 0;
  padding: 2.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 20px;
  border: 2px solid #dee2e6;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
`;

const ExampleTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.75rem;
`;

const ExampleDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const ExampleCodeBlock = styled.div`
  background: #2d3748;
  border-radius: 16px;
  padding: 2rem;
  border: 2px solid #4a5568;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const ExampleCodeContent = styled.pre`
  color: #e2e8f0;
  font-family: var(--font-stack-mono);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
  white-space: pre;
  overflow-x: auto;
  text-align: left;
  padding-left: 0;
`;

const HelpSection = styled.div`
  margin: 4rem 0;
  padding: 2.5rem;
  background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
  border-radius: 20px;
  border: 2px solid #cbd5e0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  @media (max-width: 900px) {
    padding: 1.5rem 0.5rem;
    border-radius: 16px;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.1rem 2rem 0.1rem;
    border-radius: 12px;
    margin: 2rem 0;
  }
`;

const HelpTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 1rem;
`;

const HelpDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #666;
  margin-bottom: 2.5rem;
  line-height: 1.6;
`;

const HelpOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.1rem;
  }
`;

const HelpOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 2.5rem 2rem;
  background: white;
  border-radius: 16px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 240px;
  min-height: 200px;
  box-sizing: border-box;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  @media (max-width: 900px) {
    min-width: 0;
    min-height: 140px;
    padding: 1.2rem 0.5rem;
    gap: 1rem;
  }
  @media (max-width: 600px) {
    min-width: 0;
    min-height: 100px;
    padding: 0.7rem 0.1rem;
    gap: 0.7rem;
    margin-bottom: 0.7rem;
  }
`;

const HelpIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
`;

const HelpContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HelpOptionTitle = styled.h4`
  font-family: "Inter", sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 0.75rem;
`;

const HelpOptionDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1rem;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const HelpLink = styled.a`
  color: #cdaf63;
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const SuccessSection = styled.div`
  margin: 4rem 0 0 0;
  padding: 3.5rem 2rem 3.5rem 2rem;
  background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
  border-radius: 24px;
  border: 2.5px solid #cdaf63;
  box-shadow: 0 8px 32px rgba(205, 175, 99, 0.1);
  max-width: 900px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  @media (max-width: 900px) {
    padding: 2rem 0.5rem;
    border-radius: 18px;
    max-width: 98vw;
  }
  @media (max-width: 600px) {
    padding: 1.2rem 0.1rem 2.5rem 0.1rem;
    border-radius: 14px;
    margin: 2rem 0;
    max-width: 100vw;
  }
`;

const SuccessTitle = styled.h3`
  font-family: "Inter", sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 1rem;
`;

const SuccessDescription = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #666;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const SuccessFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2.2rem;
  justify-items: center;
  align-items: center;
  margin-top: 2.5rem;
  width: 100%;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    gap: 1.2rem;
    max-width: 98vw;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    gap: 1.1rem;
    margin-top: 1.2rem;
    max-width: 100vw;
    padding: 0 0.1rem;
  }
`;

const SuccessFeature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  min-width: 0;
  min-height: 180px;
  background: linear-gradient(135deg, #fffbe6 0%, #f9f6ef 100%);
  border-radius: 20px;
  border: 2.5px solid #cdaf63;
  box-shadow:
    0 8px 32px rgba(205, 175, 99, 0.13),
    0 2px 8px rgba(0, 0, 0, 0.06);
  gap: 1.2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-align: center;
  position: relative;
  overflow: hidden;
  padding: 1.5rem 1.2rem 1.2rem 1.2rem;
  box-sizing: border-box;
  &:hover {
    transform: translateY(-6px) scale(1.03);
    box-shadow:
      0 12px 40px #cdaf63,
      0 2px 8px rgba(205, 175, 99, 0.13);
    border-color: #b8943f;
    background: linear-gradient(135deg, #fffbe6 0%, #f4e4b3 100%);
  }
  @media (max-width: 900px) {
    max-width: 98vw;
    min-height: 140px;
    gap: 1rem;
    padding: 1rem 0.5rem 0.8rem 0.5rem;
  }
  @media (max-width: 600px) {
    max-width: 98vw;
    min-width: 0;
    min-height: 100px;
    gap: 0.7rem;
    padding: 0.7rem 0.1rem 0.5rem 0.1rem;
    margin-bottom: 0.7rem;
  }
`;

const FeatureLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  background: radial-gradient(circle at 60% 40%, #fffbe6 60%, #f9f6ef 100%);
  border-radius: 50%;
  border: 2.5px solid #cdaf63;
  box-shadow:
    0 4px 18px rgba(205, 175, 99, 0.13),
    0 1px 4px rgba(0, 0, 0, 0.06);
  margin-bottom: 1.1rem;
  transition:
    box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.2s;
  img {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  }
  ${SuccessFeature}:hover & {
    box-shadow:
      0 8px 32px #cdaf63,
      0 2px 8px rgba(205, 175, 99, 0.13);
    border-color: #b8943f;
    transform: scale(1.08);
  }
  @media (max-width: 900px) {
    width: 48px;
    height: 48px;
    margin-bottom: 0.7rem;
    img {
      width: 28px;
      height: 28px;
    }
  }
  @media (max-width: 600px) {
    width: 38px;
    height: 38px;
    margin-bottom: 0.5rem;
    img {
      width: 20px;
      height: 20px;
    }
  }
`;

const FeatureText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 1.15rem;
  color: #222;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
  @media (max-width: 900px) {
    font-size: 1.05rem;
  }
  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const FeatureIcon = styled.div`
  font-size: 6rem;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 6rem !important;
  }

  .anticon svg {
    width: 6rem !important;
    height: 6rem !important;
  }

  @media (max-width: 900px) {
    font-size: 5rem;
    margin-bottom: 0.7rem;

    .anticon {
      font-size: 5rem !important;
    }

    .anticon svg {
      width: 5rem !important;
      height: 5rem !important;
    }
  }
  @media (max-width: 600px) {
    font-size: 4rem;
    margin-bottom: 0.5rem;

    .anticon {
      font-size: 4rem !important;
    }

    .anticon svg {
      width: 4rem !important;
      height: 4rem !important;
    }
  }
`;

const LastSectionSpacer = styled.div`
  display: none;
  @media (max-width: 600px) {
    display: block;
    height: 2.5rem;
    width: 100%;
  }
`;
